import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useAddTextVersion,
  useConsentSettings,
  useConsentTypes,
  useUpsertSetting,
  type ConsentType,
} from '../api/consent';
import { OrgConsentAdditions } from './org-consent-additions';

function latestBody(t: ConsentType, orgId: string | null): string {
  const versions = (t.text_versions ?? []).filter((v) => v.organisation_id === orgId);
  if (versions.length === 0) return '';
  return [...versions].sort((a, b) => b.version - a.version)[0].body;
}

export function OrgConsentPage() {
  const { orgId = '', productId = '' } = useParams<{ orgId: string; productId: string }>();
  const defaultsQuery = useConsentTypes(productId);
  const settingsQuery = useConsentSettings(orgId, productId);
  const upsert = useUpsertSetting(orgId, productId);
  const addVersion = useAddTextVersion(productId);
  const [editing, setEditing] = useState<ConsentType | null>(null);
  const [overrideText, setOverrideText] = useState('');
  const [open, { open: openModal, close: closeModal }] = useDisclosure(false);

  function startOverride(t: ConsentType) {
    setEditing(t);
    setOverrideText(latestBody(t, orgId) || latestBody(t, null));
    openModal();
  }
  function saveOverride() {
    if (!editing) return;
    addVersion.mutate(
      { id: editing.id, body: overrideText, organisation_id: orgId },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Saved.' });
          closeModal();
        },
        onError: () =>
          notifications.show({ color: 'red', message: 'Could not save consent text.' }),
      },
    );
  }

  if (defaultsQuery.isPending || settingsQuery.isPending) return <Text p="md">Loading…</Text>;

  // product defaults = those with organisation_id === null
  const defaults = (defaultsQuery.data ?? []).filter((t) => t.organisation_id === null);
  const settings = settingsQuery.data ?? [];
  const settingFor = (typeId: string) => settings.find((s) => s.consent_type_id === typeId);

  function setEnabled(typeId: string, enabled: boolean) {
    upsert.mutate(
      { organisation_id: orgId, product_id: productId, consent_type_id: typeId, enabled },
      {
        onSuccess: () => notifications.show({ color: 'green', message: 'Saved.' }),
        onError: () =>
          notifications.show({ color: 'red', message: 'Could not save consent setting.' }),
      },
    );
  }
  function setBlockingOverride(typeId: string, blocking: boolean) {
    upsert.mutate(
      { organisation_id: orgId, product_id: productId, consent_type_id: typeId, blocking },
      {
        onSuccess: () => notifications.show({ color: 'green', message: 'Saved.' }),
        onError: () =>
          notifications.show({ color: 'red', message: 'Could not save consent setting.' }),
      },
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Consent configuration</Title>
        <Button variant="subtle" component={Link} to={`/orgs/${orgId}/products`}>
          Back to products
        </Button>
      </Group>

      <Card withBorder padding="lg">
        {defaults.length === 0 ? (
          <Text c="dimmed">No default consents for this product yet.</Text>
        ) : (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Default flavour</Table.Th>
                <Table.Th>Effective text</Table.Th>
                <Table.Th />
                <Table.Th>Enabled</Table.Th>
                <Table.Th>Blocking (override)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {defaults.map((t) => {
                const s = settingFor(t.id);
                const enabled = s?.enabled ?? true;
                const effectiveBlocking = s?.blocking ?? t.blocking;
                const orgText = latestBody(t, orgId);
                const effectiveText = orgText || latestBody(t, null);
                return (
                  <Table.Tr key={t.id}>
                    <Table.Td>{t.code}</Table.Td>
                    <Table.Td>
                      <Badge color={t.blocking ? 'red' : 'gray'} variant="light">
                        {t.blocking ? 'blocking' : 'non-blocking'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2}>
                        {effectiveText || '—'}
                      </Text>
                      <Badge size="xs" color={orgText ? 'blue' : 'gray'} variant="light">
                        {orgText ? 'override' : 'inherited'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="subtle" onClick={() => startOverride(t)}>
                        Override text
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={enabled}
                        onChange={(e) => setEnabled(t.id, e.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={effectiveBlocking}
                        onChange={(e) => setBlockingOverride(t.id, e.currentTarget.checked)}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <OrgConsentAdditions orgId={orgId} productId={productId} />

      <Modal opened={open} onClose={closeModal} title={`Override text — ${editing?.code ?? ''}`}>
        <Stack>
          <Textarea
            label="Organisation consent text"
            autosize
            minRows={3}
            value={overrideText}
            onChange={(e) => setOverrideText(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={saveOverride} loading={addVersion.isPending} disabled={!overrideText}>
              Save override
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
